import { AxiosResponse } from 'axios'
import { IFilter, UpdateBody, Where } from 'types/common'
import { api } from 'API'
import { IWorkPlan, IWorkPlanWithRelations, IWorkStation, IWorkPlanDetailWithStations, IStationDetail, IDuplicatePlanResponse } from 'interfaces/plan'

export async function getLineBalanceById(
  lineBalanceId: number,
  filter: IFilter<IWorkPlan> = {}
): Promise<IWorkPlanDetailWithStations> {
  const response: AxiosResponse = await api.get(`/line-balances/${lineBalanceId}?filter=${JSON.stringify(filter)}`)
  return response.data
}

export async function getAllLineBalances(filter: IFilter<IWorkPlan> = {}): Promise<IWorkPlanWithRelations[]> {
  const response: AxiosResponse = await api.get('/line-balances', {
    params: { filter: JSON.stringify(filter) }
  })
  return response.data
}

export async function searchLineBalances(filterDto: {
  page?: number
  pageSize?: number
  search?: string
  sortBy?: string
  organizationId?: number
  isArchived?: boolean
  createdAt?: string
  creatorIds?: number[]
}): Promise<{ results: (IWorkPlanWithRelations & { workStationCount: number })[], totalCount: number }> {
  const response: AxiosResponse = await api.post('/line-balances/search', filterDto)
  return response.data
}

export async function countLineBalances(where: Where<IWorkPlan> = {}): Promise<number> {
  const response: AxiosResponse = await api.get('/line-balances/count', {
    params: { where: JSON.stringify(where) }
  })
  return response?.data?.count ?? 0
}

export async function createLineBalance(createDto: {
  name: string
  description?: string
  organizationId: number
}): Promise<IWorkPlan & { id: number }> {
  const response: AxiosResponse = await api.post('/line-balances', createDto)
  return response.data
}

export async function updateLineBalanceById(id: number, updateBody: UpdateBody<IWorkPlan>): Promise<void> {
  await api.patch(`/line-balances/${id}`, updateBody)
}

export async function deleteLineBalanceById(id: number): Promise<void> {
  await api.delete(`/line-balances/${id}`)
}


export async function archiveMultipleLineBalances(ids: number[]): Promise<void> {
  await api.patch('/line-balances/archive-multiple', { ids })
}

export async function restoreLineBalanceById(id: number): Promise<void> {
  await api.patch(`/line-balances/${id}/restore`)
}

export async function restoreMultipleLineBalances(ids: number[]): Promise<void> {
  await api.patch('/line-balances/restore-multiple', { ids })
}

export async function createWorkStation(lineBalanceId: number): Promise<IWorkStation> {
  const response: AxiosResponse = await api.post('/work-stations', { lineBalanceId })
  return response.data
}

export async function linkStepsToWorkStation(payload: { 
  workStationId: number; 
  stepIds: number[];
  allStationStepPositions: { id: number; position: number }[];
  newStepIds: number[];
}) {
  const response: AxiosResponse = await api.post('/steps-work-stations', payload)
  return response.data
}

export async function addStepsToEndOfStation(payload: { 
  workStationId: number; 
  stepIds: number[];
}): Promise<void> {
  await api.post('/steps-work-stations/add-to-end', payload)
}

export async function unlinkStepFromWorkStation(workStationId: number, stepId: number): Promise<void> {
  await api.delete(`/steps-work-stations/${workStationId}`, { data: { stepIds: [stepId] } })
}

export async function unlinkStepsFromWorkStation(workStationId: number, stepIds: number[]): Promise<void> {
  await api.delete(`/steps-work-stations/${workStationId}`, { data: { stepIds } })
}

export async function deleteWorkStation(workStationId: number): Promise<void> {
  await api.delete(`/work-stations/${workStationId}`)
}

export async function updateWorkStationById(
  id: number,
  updateBody: Partial<Pick<IWorkStation, 'name' | 'duration'>>
): Promise<void> {
  await api.patch(`/work-stations/${id}`, updateBody)
}

export async function updateStepsPositionsInStation(
  workStationId: number,
  stepPositions: { id: number; position: number }[]
): Promise<void> {
  await api.patch('/steps-work-stations/positions/batch', {
    workStationId,
    stepPositions
  })
}

export async function moveStepBetweenStations(payload: {
  stepIds: number[]
  fromStationId: number
  toStationId: number
  newStationStepPositions: { id: number; position: number }[]
}): Promise<void> {
  await api.patch('/steps-work-stations/move-step', payload)
}

export async function getStationDetailById(stationId: number): Promise<IStationDetail> {
  const response: AxiosResponse = await api.get(`/work-stations/${stationId}`)
  return response.data
}

export async function createWorkplanVariant(originId: number, name?: string): Promise<IDuplicatePlanResponse> {
  const response: AxiosResponse = await api.post(`/line-balances/${originId}/variants`, { name })
  return response.data
}

export async function duplicateWorkplan(sourceId: number, name?: string): Promise<IDuplicatePlanResponse> {
  const response: AxiosResponse = await api.post(`/line-balances/${sourceId}/duplicate`, { name })
  return response.data
}

export interface ReviewSettings {
  requireChangesReview: boolean
  reviewOption?: number
}

export async function updateReviewSettings(
  workplanId: number,
  settings: ReviewSettings
): Promise<void> {
  await api.patch(`/line-balances/${workplanId}/review-settings`, settings)
}

// Edit lock APIs
export async function lockLineBalance(lineBalanceId: number): Promise<{ editLockOwnerId: number; editLockExpirationAt: string }> {
  const response: AxiosResponse = await api.post(`/line-balances/${lineBalanceId}/lock`)
  return response.data
}

export async function extendLockLineBalance(lineBalanceId: number): Promise<{ editLockExpirationAt: string }> {
  const response: AxiosResponse = await api.post(`/line-balances/${lineBalanceId}/extend-lock`)
  return response.data
}

export async function releaseLockLineBalance(lineBalanceId: number): Promise<void> {
  await api.post(`/line-balances/${lineBalanceId}/release-lock`)
}

export async function forceLockLineBalance(lineBalanceId: number): Promise<{ editLockOwnerId: number; editLockExpirationAt: string }> {
  const response: AxiosResponse = await api.post(`/line-balances/${lineBalanceId}/force-lock`)
  return response.data
}